/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Check,
  MapPin,
  MessageSquare,
  Phone,
  ShieldCheck,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useSOSEventContext } from "@/contexts/SOSEventContext";
import { useSafeCircleProfiles } from "@/hooks/useSafeCircleProfiles";
import { useSOSPlaces } from "@/hooks/useSOSPlaces";
import { useSafeHavens } from "@/hooks/useSafeHavens";
import { useEmergencyContacts } from "@/hooks/useEmergencyContacts";
import {
  buildEmergencySms,
  buildEmergencySmsHref,
} from "@/utils/safecircle/emergencySms";

export default function SOSActiveScreen() {
  const { t } = useTranslation();
  const {
    event,
    countdownRemainingMs,
    status,
    responses,
    smsStatus,
    cancel,
    resolve,
    confirmNow,
  } = useSOSEventContext();
  const { profiles } = useSafeCircleProfiles();
  const profile = profiles.find((p) => p.profileId === event?.profileId) ?? null;
  const lat = event?.lat ?? null;
  const lng = event?.lng ?? null;
  const { places } = useSOSPlaces(lat, lng);
  const { havens: safeHavens } = useSafeHavens(lat, lng, 2);
  const { contacts } = useEmergencyContacts();

  const [pinInput, setPinInput] = useState("");
  const [cancelError, setCancelError] = useState<string | null>(null);

  const isCountdown = status === "countdown";
  const shortCode = event?.eventId ? event.eventId.slice(-6) : "";

  const smsBody = useMemo(() => {
    if (lat == null || lng == null) return "";
    return buildEmergencySms({
      lat,
      lng,
      eventShortCode: shortCode,
      profile,
      includeMedical: true,
    });
  }, [lat, lng, shortCode, profile]);

  if (!event) return null;

  const handleCancel = async () => {
    setCancelError(null);
    const requiresPin =
      !isCountdown && profile?.stealthConfig?.pin ? true : false;
    const { ok, reason } = await cancel(requiresPin ? pinInput : undefined);
    if (!ok) {
      setCancelError(
        reason === "invalid-pin"
          ? t("safeCircle.sos.pinInvalid")
          : t("safeCircle.sos.cancelFailed"),
      );
    }
  };

  const responsesArr = Object.values(responses);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[70] bg-background/95 backdrop-blur-xl overflow-y-auto"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      <div className="w-full max-w-lg mx-auto px-4 pt-6 pb-24 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-destructive/20 border border-destructive/50 flex items-center justify-center">
            <AlertTriangle size={22} className="text-destructive animate-pulse" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold font-display">
              {isCountdown
                ? t("safeCircle.sos.countdownTitle")
                : t("safeCircle.sos.activeTitle")}
            </h2>
            <p className="text-xs text-muted-foreground">
              {isCountdown
                ? t("safeCircle.sos.countdownSubtitle", {
                    seconds: Math.ceil(countdownRemainingMs / 1000),
                  })
                : t("safeCircle.sos.activeSubtitle", { code: shortCode })}
            </p>
          </div>
        </div>

        {isCountdown ? (
          <div className="glass-card p-6 flex flex-col items-center gap-4 border border-destructive/40 bg-destructive/10">
            <div className="text-5xl font-bold tabular-nums">
              {Math.ceil(countdownRemainingMs / 1000)}
            </div>
            <p className="text-xs text-muted-foreground text-center">
              {t("safeCircle.sos.countdownHelp")}
            </p>
            <div className="flex gap-2 w-full">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-3 rounded-xl text-sm font-medium bg-muted/40 hover:bg-muted/60 flex items-center justify-center gap-1"
              >
                <X size={14} /> {t("safeCircle.sos.cancel")}
              </button>
              <button
                type="button"
                onClick={confirmNow}
                className="flex-1 py-3 rounded-xl text-sm font-medium bg-destructive text-destructive-foreground hover:bg-destructive/90 flex items-center justify-center gap-1"
              >
                <AlertTriangle size={14} /> {t("safeCircle.sos.sendNow")}
              </button>
            </div>
          </div>
        ) : (
          <div className="glass-card p-4 space-y-3 border border-destructive/40">
            <div className="flex items-center gap-2 text-sm">
              <MapPin size={14} className="text-destructive" />
              <span className="font-mono">
                {lat != null && lng != null ? (
                  <>
                    {lat.toFixed(5)}, {lng.toFixed(5)}
                  </>
                ) : (
                  t("safeCircle.sos.locationUnavailable")
                )}
              </span>
            </div>
            {/* #2/#3: status-aware SMS state instead of an unconditional
                "help is on the way". A failed/no-location send offers the
                native SMS composer as a fallback so the user can still
                reach their contact. */}
            {smsStatus === "failed" || smsStatus === "no-location" ? (
              <div className="space-y-2">
                <p className="text-xs text-destructive font-medium">
                  {smsStatus === "no-location"
                    ? t("safeCircle.sos.smsSkippedNoLocation")
                    : t("safeCircle.sos.smsFailed")}
                </p>
                {smsBody && contacts[0]?.phone ? (
                  <a
                    href={buildEmergencySmsHref(contacts[0].phone, smsBody)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-destructive underline"
                  >
                    <MessageSquare size={12} />
                    {t("safeCircle.sos.smsFallbackCta")}
                  </a>
                ) : null}
              </div>
            ) : smsStatus === "no-contacts" ? (
              <p className="text-xs text-muted-foreground">
                {t("safeCircle.sos.smsNoContacts")}
              </p>
            ) : smsStatus === "sending" ? (
              <p className="text-xs text-muted-foreground">
                {t("safeCircle.sos.smsSending")}
              </p>
            ) : smsStatus === "sent" ? (
              <p className="text-xs text-muted-foreground">
                {t("safeCircle.sos.helpOnTheWay")}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("safeCircle.sos.helpOnTheWay")}
              </p>
            )}
          </div>
        )}

        {!isCountdown && (
          <div className="glass-card p-4">
            <h3 className="text-sm font-semibold mb-3">
              {t("safeCircle.sos.circleResponses")}
            </h3>
            {responsesArr.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                {t("safeCircle.sos.noResponsesYet")}
              </p>
            ) : (
              <ul className="space-y-1">
                {responsesArr.map((r) => (
                  <li
                    key={r.uid}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="font-mono truncate">{r.uid.slice(0, 8)}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] ${
                        r.response
                          ? "bg-success/20 text-success"
                          : "bg-muted/40 text-muted-foreground"
                      }`}
                    >
                      {r.response ?? t("safeCircle.sos.notified")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {profile?.medicalInfo && (
          <div className="glass-card p-4 border border-amber-500/30 bg-amber-500/10">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-1">
              <ShieldCheck size={14} className="text-amber-500" />
              {t("safeCircle.sos.medicalInfo")}
            </h3>
            <dl className="space-y-1 text-xs">
              {profile.medicalInfo.bloodType && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t("safeCircle.sos.bloodType")}
                  </dt>
                  <dd className="font-medium">{profile.medicalInfo.bloodType}</dd>
                </div>
              )}
              {profile.medicalInfo.allergies?.length > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t("safeCircle.sos.allergies")}
                  </dt>
                  <dd className="font-medium truncate max-w-[60%] text-right">
                    {profile.medicalInfo.allergies.join(", ")}
                  </dd>
                </div>
              )}
              {profile.medicalInfo.medications?.length > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">
                    {t("safeCircle.sos.medications")}
                  </dt>
                  <dd className="font-medium truncate max-w-[60%] text-right">
                    {profile.medicalInfo.medications.join(", ")}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {!isCountdown && lat != null && lng != null && (
          <a
            href={buildEmergencySmsHref("911", smsBody)}
            className="w-full glass-card p-4 flex items-center gap-3 hover:bg-muted/40 border border-primary/30"
          >
            <div className="shrink-0 w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              <MessageSquare size={18} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold">
                {t("safeCircle.sos.shareWith911")}
              </p>
              <p className="text-xs text-muted-foreground truncate">{smsBody}</p>
              <p className="text-[11px] text-primary/80 mt-0.5">
                {t("safeCircle.sos.tapToShare")}
              </p>
            </div>
          </a>
        )}

        {places.hospitals.length > 0 && (
          <PlaceSection
            title={t("sos.hospitals")}
            items={places.hospitals.slice(0, 3)}
          />
        )}
        {places.police.length > 0 && (
          <PlaceSection
            title={t("sos.police")}
            items={places.police.slice(0, 3)}
          />
        )}

        {safeHavens.length > 0 && (
          <div className="glass-card p-4">
            <h3 className="text-sm font-semibold mb-3">
              {t("safeCircle.sos.nearbySafeHavens")}
            </h3>
            <ul className="space-y-2">
              {safeHavens.slice(0, 3).map((h) => (
                <li
                  key={h.id}
                  className="flex items-center justify-between text-xs"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{h.name}</p>
                    <p className="text-muted-foreground truncate">
                      {h.address}
                    </p>
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${h.lat},${h.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-full bg-primary/15 text-primary"
                  >
                    <MapPin size={14} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {contacts.length > 0 && (
          <div className="glass-card p-4">
            <h3 className="text-sm font-semibold mb-3">
              {t("safeCircle.sos.emergencyContacts")}
            </h3>
            <ul className="space-y-2">
              {contacts.map((c) => (
                <li key={c.id}>
                  <a
                    href={`tel:${c.phone}`}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="font-medium">{c.name}</span>
                    <span className="flex items-center gap-1 text-success">
                      <Phone size={12} />
                      {c.phone}
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {!isCountdown && (
          <div className="glass-card p-4 border border-muted-foreground/20 space-y-2">
            {profile?.stealthConfig?.pin && (
              <input
                type="password"
                value={pinInput}
                onChange={(e) => setPinInput(e.target.value)}
                placeholder={t("safeCircle.sos.pinPrompt")}
                className="w-full bg-transparent border border-muted-foreground/30 rounded-xl px-3 py-2 text-sm"
              />
            )}
            {cancelError && (
              <p className="text-xs text-destructive">{cancelError}</p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium bg-muted/40 hover:bg-muted/60"
              >
                {t("safeCircle.sos.cancel")}
              </button>
              <button
                type="button"
                onClick={resolve}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium gradient-bg text-primary-foreground flex items-center justify-center gap-1"
              >
                <Check size={14} />
                {t("safeCircle.sos.markResolved")}
              </button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}

function PlaceSection({
  title,
  items,
}: {
  title: string;
  items: { name: string; lat?: number; lng?: number; phone?: string }[];
}) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-sm font-semibold mb-3">{title}</h3>
      <ul className="space-y-2">
        {items.map((item, idx) => {
          const href = item.phone
            ? `tel:${item.phone}`
            : item.lat != null && item.lng != null
              ? `https://www.google.com/maps/dir/?api=1&destination=${item.lat},${item.lng}`
              : "#";
          const isTel = href.startsWith("tel:");
          return (
            <li
              key={`${item.name}-${idx}`}
              className="flex items-center justify-between text-xs"
            >
              <span className="truncate">{item.name}</span>
              <a
                href={href}
                target={isTel ? undefined : "_blank"}
                rel={isTel ? undefined : "noopener noreferrer"}
                className={`p-2 rounded-full ${
                  isTel ? "bg-success/15 text-success" : "bg-primary/15 text-primary"
                }`}
              >
                {isTel ? <Phone size={12} /> : <MapPin size={12} />}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
